import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import { http } from '../lib/http';

interface RobloxLoginButtonProps {
  onError?: (error: string) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  flow?: 'login' | 'register'; // Specify whether this is for login or registration
  variant?: 'contained' | 'outlined' | 'text';
}

const RobloxLoginButton: React.FC<RobloxLoginButtonProps> = ({ 
  onError,
  fullWidth = false,
  disabled = false,
  flow = 'login', // Default to login
  variant = 'outlined'
}) => {
  const [loading, setLoading] = useState(false);

  const handleRobloxOAuth = async () => {
    try {
      setLoading(true);

      // For login flow, try silent login first if we have a stored Roblox ID
      if (flow === 'login') {
        const storedRobloxId = localStorage.getItem('roblox_id');
        console.log('[Roblox OAuth] Checking for stored Roblox ID:', storedRobloxId ? 'Found' : 'Not found');
        
        if (storedRobloxId) {
          console.log('[Roblox OAuth] Attempting silent login with Roblox ID:', storedRobloxId);
          
          try {
            const silentResponse = await http.post('/auth/roblox/silent-login', {
              robloxId: storedRobloxId
            });

            console.log('[Roblox OAuth] Silent login response:', silentResponse.data);

            if (silentResponse.data.success) {
              // Silent login successful! Token is set in httpOnly cookie by server
              console.log('[Roblox OAuth] ✅ Silent login successful! No authorization needed.');
              
              // Force page reload to refresh auth state
              window.location.href = '/';
              return;
            } else if (silentResponse.data.error) {
              // Handle specific errors (pending approval, rejected, etc.)
              console.log('[Roblox OAuth] Silent login error:', silentResponse.data.error);
              throw new Error(silentResponse.data.error);
            }
            
            // Silent login failed, need authorization - continue to normal flow
            console.log('[Roblox OAuth] Silent login failed (needsAuthorization), proceeding with authorization');
          } catch (error: any) {
            if (error.message === 'pending_approval' || error.message === 'account_rejected') {
              // Re-throw these errors to be handled below
              throw error;
            }
            // For other errors, just continue to normal OAuth flow
            console.log('[Roblox OAuth] Silent login error, falling back to authorization:', error.message);
          }
        } else {
          console.log('[Roblox OAuth] No stored Roblox ID, proceeding with full OAuth flow');
        }
      }

      // Normal OAuth flow (or fallback from failed silent login)
      const response = await http.get(`/auth/roblox/authorize?flow=${flow}`);

      if (response.data.success) {
        const { authUrl, state } = response.data.data;
        
        // Store state for CSRF verification
        sessionStorage.setItem('roblox_oauth_state', state);
        sessionStorage.setItem('roblox_oauth_flow', flow);
        
        // Redirect to Roblox OAuth authorization page
        window.location.href = authUrl;
      } else {
        const errorMsg = typeof response.data.error === 'string' 
          ? response.data.error 
          : 'Failed to get authorization URL';
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      console.error('[Roblox OAuth] Error:', error);
      const apiError = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : null;
      const errorMessage = apiError || error.message || `Failed to initiate Roblox ${flow}`;
      if (onError) {
        onError(errorMessage);
      }
      setLoading(false);
    }
  };

  const buttonText = flow === 'register' ? 'Register with Roblox' : 'Sign in with Roblox';
  const loadingText = flow === 'register' ? 'Connecting to Roblox...' : 'Signing in...';

  return (
    <Button
      fullWidth={fullWidth}
      variant={variant}
      onClick={handleRobloxOAuth}
      disabled={disabled || loading}
      sx={{
        py: 1.75,
        borderColor: '#00a2ff',
        color: variant === 'contained' ? '#ffffff' : '#00a2ff',
        backgroundColor: variant === 'contained' ? '#00a2ff' : 'transparent',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: variant === 'contained' ? '0 4px 12px rgba(0, 162, 255, 0.3)' : 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: '#0088d9',
          backgroundColor: variant === 'contained' ? '#0088d9' : 'rgba(0, 162, 255, 0.08)',
          transform: 'translateY(-2px)',
          boxShadow: variant === 'contained' ? '0 6px 16px rgba(0, 162, 255, 0.4)' : 'none',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={22} sx={{ color: variant === 'contained' ? '#ffffff' : '#00a2ff' }} />
          <span>{loadingText}</span>
        </>
      ) : (
        <>
          <Icon 
            icon="simple-icons:roblox" 
            width="24" 
            height="24"
            style={{ 
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          <span>{buttonText}</span>
        </>
      )}
    </Button>
  );
};

export default RobloxLoginButton;
